/**
 * AI Conversation Service
 * Integration with backend API for intelligent English conversation practice
 */

import { AI_CONFIG } from '../config/ai-config';

export interface AIMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface AIConversationContext {
  level: 'beginner' | 'intermediate' | 'advanced';
  topic?: string;
  previousMessages: AIMessage[];
}

export class AIConversationService {
  private backendUrl: string = AI_CONFIG.BACKEND_URL;
  private model: string = AI_CONFIG.DEFAULT_MODEL;
  private maxTokens: number = AI_CONFIG.MAX_TOKENS;
  private useDirectAPI: boolean = AI_CONFIG.DIRECT_API_ENABLED;

  setBackendUrl(url: string) {
    this.backendUrl = url;
  }

  setModel(modelName: string) {
    this.model = modelName;
  }

  setDirectAPI(enabled: boolean) {
    this.useDirectAPI = enabled;
  }

  /**
   * Smart mode selection: Try proxy first, fallback to direct API
   */
  async sendMessage(
    userMessage: string,
    context: AIConversationContext
  ): Promise<string> {
    const systemPrompt = this.getSystemPrompt(context.level, context.topic);

    const messages: AIMessage[] = [
      { role: 'system', content: systemPrompt },
      ...context.previousMessages,
      { role: 'user', content: userMessage }
    ];

    // If direct API is enabled and configured, use it
    if (this.useDirectAPI && AI_CONFIG.DIRECT_API_URL && AI_CONFIG.DIRECT_API_KEY) {
      return this.sendDirectMessage(messages);
    }

    // Otherwise, try proxy (backend server)
    return this.sendProxyMessage(messages);
  }

  /**
   * Direct API call (for mobile access)
   * ⚠️ WARNING: API Key is exposed - use only for personal use
   */
  private async sendDirectMessage(messages: AIMessage[]): Promise<string> {
    try {
      console.log('Using direct API:', AI_CONFIG.DIRECT_API_URL);

      const response = await fetch(AI_CONFIG.DIRECT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AI_CONFIG.DIRECT_API_KEY}`
        },
        body: JSON.stringify({
          model: this.model,
          messages: messages,
          max_tokens: this.maxTokens,
          temperature: AI_CONFIG.TEMPERATURE,
          stream: false
        }),
        signal: AbortSignal.timeout(30000) // 30 seconds timeout (increased for mobile)
      });

      console.log('Direct API response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Direct API error:', errorData);
        throw new Error(errorData.error?.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('Direct API response:', data);

      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('AI 返回格式错误');
      }

      return data.choices[0].message.content;
    } catch (error: any) {
      console.error('Direct API error:', error);

      if (error.name === 'AbortError' || error.name === 'TimeoutError') {
        throw new Error('请求超时，请检查网络连接');
      }

      if (error.message.includes('Failed to fetch') || error.message.includes('Load failed')) {
        throw new Error('无法连接到 API 服务器');
      }

      throw new Error(error.message || 'AI 服务暂时不可用');
    }
  }

  /**
   * Proxy API call through backend server (more secure)
   */
  private async sendProxyMessage(messages: AIMessage[]): Promise<string> {
    try {
      const proxyUrl = `${this.backendUrl}/api/ai/chat`;

      console.log('Using proxy:', proxyUrl);

      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Access-Password': AI_CONFIG.ACCESS_PASSWORD
        },
        body: JSON.stringify({
          model: this.model,
          messages: messages,
          maxTokens: this.maxTokens,
          temperature: 0.7
        }),
        signal: AbortSignal.timeout(30000) // 30 seconds timeout (increased for mobile)
      });

      console.log('Proxy response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Proxy error:', errorData);

        if (errorData.error === 'API key not configured') {
          throw new Error('服务器未配置 API 密钥');
        }

        throw new Error(errorData.error?.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log('Proxy response:', data);

      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('AI 返回格式错误');
      }

      return data.choices[0].message.content;
    } catch (error: any) {
      console.error('Proxy error:', error);

      if (error.name === 'AbortError' || error.name === 'TimeoutError') {
        throw new Error('代理服务器超时，请稍后再试');
      }

      if (error.message.includes('Failed to fetch') || error.message.includes('Load failed')) {
        throw new Error('无法连接到代理服务器，请检查网络或使用直连模式');
      }

      throw new Error(error.message || '代理服务暂时不可用');
    }
  }

  private getSystemPrompt(level: string, topic?: string): string {
    const levelInstructions = {
      beginner: `
        You are a friendly English teacher helping a beginner student practice English.
        - Use very simple words and short sentences
        - Speak slowly and clearly
        - Use common everyday vocabulary
        - Give encouragement and praise
        - Correct mistakes gently
        - Keep conversations about daily life (family, food, school, hobbies)
      `,
      intermediate: `
        You are a supportive English tutor helping an intermediate student improve their English.
        - Use moderate vocabulary and compound sentences
        - Discuss topics like travel, hobbies, future plans, opinions
        - Explain new words if used
        - Ask follow-up questions to encourage practice
        - Provide gentle corrections
      `,
      advanced: `
        You are an English conversation partner for an advanced student.
        - Use rich vocabulary and complex sentence structures
        - Discuss abstract topics, current events, culture, ideas
        - Challenge the student with thought-provoking questions
        - Provide detailed feedback on language use
        - Engage in debates and discussions
      `
    };

    let prompt = levelInstructions[level as keyof typeof levelInstructions];

    if (topic) {
      prompt += `\n\nCurrent conversation topic: ${topic}`;
    }

    prompt += `\n\nImportant rules:
      - Always respond in English
      - Keep responses concise (2-3 sentences for beginners, 3-4 for others)
      - Be encouraging and positive
      - If the student makes a mistake, correct it gently at the end
    `;

    return prompt;
  }
}

export const aiConversationService = new AIConversationService();
